import { BoxOfferDetailedEntity, BoxOfferDraftEntity } from "@modules/boxOffer/entities/boxOfferEntity"
import { BoxOfferType } from "@modules/boxOffer/entities/boxOfferTypes"
import { word } from "@modules/core/utils/i18n"
import { DefaultTheme } from "styled-components"
import { SlashedEyeLineIcon } from "@images/svgIcons/slashedEyeLineIcon"
import { HiddenPriceIcon } from "@images/svgIcons/essentials/hiddenPriceIcon"
import { MoneyBillIcon } from "@images/svgIcons/moneyBillIcon"
import { Archive } from "lucide-react"
import boxOfferMillenniumIcon from "@images/illustrations/boxOfferMillenniumIcon.svg"
import boxOfferTicketIcon from "@images/illustrations/boxOfferTicketIcon.svg"
import boxOfferPlanIcon from "@images/illustrations/boxOfferPlanIcon.svg"
import boxOfferTrialIcon from "@images/illustrations/boxOfferTrialIcon.svg"
import boxOfferDropInIcon from "@images/illustrations/boxOfferDropInIcon.svg"

/**
 * Utility function to get the icon of the box offer by type
 * @param type : BoxOfferType
 * @returns React.JSX.Element
 */
export function getBoxOfferIcon(type: BoxOfferType): React.JSX.Element {
  switch (type) {
    case BoxOfferType.Subscription:
      return <img src={boxOfferPlanIcon} alt="boxOfferPlanIcon" />
    case BoxOfferType.TicketsBook:
      return <img src={boxOfferTicketIcon} alt="boxOfferTicketIcon" />
    case BoxOfferType.Trial:
      return <img src={boxOfferTrialIcon} alt="boxOfferTrialIcon" />
    case BoxOfferType.DropIn:
      return <img src={boxOfferDropInIcon} alt="boxOfferDropInIcon" />
    case BoxOfferType.Millenium:
      return <img src={boxOfferMillenniumIcon} alt="boxOfferMillenniumIcon" />
    default:
      return <></>
  }
}
/**
 * Utility function to get the commitment of the box offer with the validityPeriodMonths or minimumMonthlyDuration
 * @param offer : BoxOfferDetailedEntity | BoxOfferDraftEntity | null
 * @returns string
 */
export const getOfferCommitment = (offer: BoxOfferDetailedEntity | BoxOfferDraftEntity | null): string => {
  if (offer && [BoxOfferType.Trial, BoxOfferType.TicketsBook, BoxOfferType.DropIn].includes(offer.type)) {
    if (offer.validityPeriodMonths) {
      return word("box.offer.form.infos.validity", { count: offer.validityPeriodMonths })
    }
    return word("box.offer.form.infos.validity.unlimited")
  } else if (offer && "minimumMonthlyDuration" in offer && offer.minimumMonthlyDuration) {
    return word("box.offer.form.infos.commitment", { count: offer.minimumMonthlyDuration })
  }
  return word("box.offer.form.infos.no.commitment")
}

export const getOfferVisibility = (offer: BoxOfferDetailedEntity | BoxOfferDraftEntity | null, theme: DefaultTheme) => {
  if (offer?.archived) {
    return {
      text: word("global.archived", { count: 1 }),
      icon: <Archive strokeWidth={1.5} size={14} color={theme.mainWhite} />,
    }
  }
  if (offer?.showcaseDisplay === false)
    return {
      text: word("box.offer.form.resume.showcaseDisplay"),
      icon: <SlashedEyeLineIcon color={theme.mainWhite} />,
    }

  if (offer?.priceDisplay === false)
    return {
      text: word("box.offer.form.resume.priceDisplay"),
      icon: <HiddenPriceIcon color={theme.mainWhite} />,
    }

  const iconMoney = <MoneyBillIcon color={theme.mainWhite} />
  switch (offer?.autonomousPurchase) {
    case "PUBLIC":
      return {
        text: word("box.offer.form.resume.public"),
        icon: iconMoney,
      }
    case "LEADS_AND_DROP_IN":
      return {
        text: word("box.offer.form.resume.leadsAndDropIn"),
        icon: iconMoney,
      }
    case "MEMBERS":
      return {
        text: word("box.offer.form.resume.members"),
        icon: iconMoney,
      }
    case "NO_ONE":
      return {
        text: word("box.offer.form.resume.noOne"),
        icon: iconMoney,
      }
  }

  return {
    text: "",
    icon: null,
  }
}
